var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useImperativeHandle, forwardRef, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import StylePropType from 'react-style-proptype';
import { cx } from 'emotion';
import { useMemoizedId } from './utils';
import InlineButton from './Utilities/InlineButton';
const noop = () => { };
const enterKeyCode = 13;
const escKeyCode = 27;
const isTextTruthy = val => val !== undefined && val !== null && val.toString() !== '';
const sizeStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    visibility: 'hidden',
    height: 0,
    overflow: 'scroll',
    whiteSpace: 'pre',
};
const sizeClasses = {
    default: '',
    h1: 'h1',
    h2: 'h2',
    h3: 'h3',
    h4: 'h4',
    h5: 'h5',
    h6: 'h6',
    small: 'small',
};
const InlineEdit = (_a) => {
    var { disabled = false, forwardedRef, id, label = '', minWidth = 130, name = '', placeholder, required = false, requiredWarningMessage = 'This Field is Required', size = 'default', type = 'text', validateInput = noop, value = '', onBlur = noop, onCancel = noop, onChange, onEditStateChange = noop, onFocus = noop, onSave, className, style } = _a, rest = __rest(_a, ["disabled", "forwardedRef", "id", "label", "minWidth", "name", "placeholder", "required", "requiredWarningMessage", "size", "type", "validateInput", "value", "onBlur", "onCancel", "onChange", "onEditStateChange", "onFocus", "onSave", "className", "style"]);
    // States for Inline Edit
    const [localValue, setLocalValue] = useState(value);
    const [showInput, setShowInput] = useState(false);
    const [inputWidth, setInputWidth] = useState();
    const [savedTimer, setSavedTimer] = useState();
    const [restingClassNames, setRestingClassNames] = useState(['resting-text']);
    const [validationError, setValidationError] = useState();
    const inputId = useMemoizedId({ id, label });
    // ensure we clear any timers in case component unmounts.
    useEffect(() => () => clearTimeout(savedTimer), [savedTimer]);
    const previousShowInput = useRef(showInput);
    useEffect(() => {
        if (previousShowInput.current !== showInput) {
            onEditStateChange(showInput);
        }
        previousShowInput.current = showInput;
    }, [showInput, onEditStateChange]);
    useEffect(() => {
        setLocalValue(value);
    }, [value]);
    useEffect(() => {
        setValidationError(validateInput(localValue));
    }, [localValue, validateInput]);
    const hasOnChange = Boolean(onChange);
    const displayValue = hasOnChange ? value : localValue;
    useEffect(() => {
        if (!isTextTruthy(displayValue) && required) {
            setValidationError(requiredWarningMessage);
        }
    }, [displayValue, required, requiredWarningMessage]);
    const inputRef = useRef();
    /**
     * This hook is used to customize the instance value exposed to the parent for the ref.
     * It must always be used with React.forwardRef. This gives us control of where to pass the ref prop.
     * Whatever ref is passed to Inline Edit will receive the inputRef instance.
     * This pattern supports all methods of creating refs.
     */
    useImperativeHandle(forwardedRef, () => inputRef.current);
    // used to calculate sizing for input to grow with text
    const valueSizeRef = useRef();
    const placeholderSizeRef = useRef();
    const errorMessageRef = useRef();
    const isTextArea = type === 'textarea';
    useEffect(() => {
        // If there's an onChange handler, the save and cancel buttons are not shown, so the additional
        //   padding is not needed. However, there's still an additional 5px that need to be added.
        const extraPadding = hasOnChange ? 5 : 70;
        const pencilPadding = 25;
        let newWidth = isTextTruthy(displayValue)
            ? valueSizeRef.current.scrollWidth
            : placeholderSizeRef.current ? placeholderSizeRef.current.scrollWidth : 0;
        const errorMessageWidth = errorMessageRef.current ? errorMessageRef.current.scrollWidth : 0;
        newWidth = Math.max(newWidth, errorMessageWidth);
        newWidth += showInput ? extraPadding : pencilPadding;
        if (newWidth < minWidth) {
            newWidth = minWidth;
        }
        setInputWidth(newWidth);
    }, [displayValue, hasOnChange, valueSizeRef, placeholderSizeRef, minWidth, showInput, validationError]);
    useEffect(() => {
        if (showInput && isTextArea) {
            // Set the cursor to the end of the text and scroll to the end
            inputRef.current.setSelectionRange(inputRef.current.textContent.length, inputRef.current.textContent.length);
            inputRef.current.scrollTop = inputRef.current.scrollHeight;
        }
    }, [isTextArea, showInput]);
    const showSuccess = () => {
        setRestingClassNames([...restingClassNames, 'saved']);
        setShowInput(false);
        setSavedTimer(setTimeout(() => setRestingClassNames(restingClassNames.filter(restingClassName => restingClassName !== 'saved')), 1000));
    };
    const handleBlur = e => {
        if (hasOnChange) {
            setShowInput(false);
            showSuccess();
        }
        onBlur(e);
    };
    const handleFocus = e => {
        onFocus(e);
    };
    const handleChange = e => {
        const inputValue = e.target.value;
        if (hasOnChange) {
            onChange(e);
        }
        else {
            setLocalValue(inputValue);
        }
    };
    const handleSaveClick = e => {
        if (!hasOnChange) {
            if (validationError) {
                return;
            }
            onSave({ value: localValue, name }, e);
        }
        showSuccess();
    };
    // Optional to handle how we cancel our input
    const handleCancelClick = e => {
        onCancel({ value: localValue, name }, e);
        setLocalValue(value);
        setShowInput(false);
    };
    const handleKeyDown = e => {
        if (e.keyCode === enterKeyCode) {
            handleSaveClick();
        }
        else if (e.keyCode === escKeyCode) {
            handleCancelClick();
        }
    };
    const handleActiveClick = () => {
        setShowInput(true);
    };
    const inputProps = Object.assign(Object.assign({}, rest), { name,
        type, value: displayValue, label,
        placeholder, id: inputId, onKeyDown: handleKeyDown, ref: inputRef, onBlur: handleBlur, onFocus: handleFocus, onChange: handleChange, autoFocus: true });
    const inputStyle = Object.assign({ marginBottom: '20px', width: showInput ? `${inputWidth}px` : null }, style);
    const parentClassName = cx('inline-edit-group', { required }, className);
    const inlineInput = (React.createElement(React.Fragment, null,
        isTextArea
            ? React.createElement("textarea", Object.assign({ className: cx(sizeClasses[size], 'form-control') }, inputProps, { style: { marginTop: '17px', padding: '16px', minWidth: '100px' } }))
            : React.createElement("input", Object.assign({ className: sizeClasses[size] }, inputProps)),
        label ? (React.createElement("label", { className: cx('control-label', { 'textarea-label': isTextArea }), htmlFor: inputId }, label)) : null,
        !hasOnChange ? (React.createElement("div", { className: 'right-addon' },
            React.createElement("button", { onClick: handleSaveClick, "aria-label": 'save', disabled: Boolean(validationError) },
                React.createElement("i", { className: 'fa fa-check' })),
            React.createElement("button", { onClick: handleCancelClick, "aria-label": 'cancel' },
                React.createElement("i", { className: 'fa fa-times' })))) : null));
    // For disabled state we add the disabled class to the parent
    // In addition we pass a <p> without the onClick handler
    const inlineDisplay = (React.createElement(InlineButton, { onClick: disabled ? () => { } : handleActiveClick, className: cx(...restingClassNames, { disabled }) },
        React.createElement("p", { className: sizeClasses[size] }, displayValue || placeholder),
        label ? React.createElement("div", { className: 'resting-label' }, label) : null));
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: parentClassName, style: inputStyle },
            showInput ? inlineInput : inlineDisplay,
            validationError ? (React.createElement("div", { className: 'error-message', ref: errorMessageRef }, validationError)) : null),
        React.createElement("div", { className: sizeClasses[size], ref: valueSizeRef, style: sizeStyle }, displayValue),
        React.createElement("div", { className: sizeClasses[size], ref: placeholderSizeRef, style: sizeStyle }, placeholder || '')));
};
InlineEdit.propTypes = {
    disabled: PropTypes.bool,
    forwardedRef: PropTypes.oneOf([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
    id: PropTypes.string,
    label: PropTypes.node,
    minWidth: PropTypes.number,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    required: PropTypes.bool,
    requiredWarningMessage: PropTypes.string,
    size: PropTypes.oneOf(['default', 'h1', 'h2', 'h3', 'h4', 'h4', 'h5', 'h6', 'small']),
    type: PropTypes.oneOf(['text', 'number', 'password', 'url', 'search', 'email', 'tel', 'textarea']),
    validateInput: PropTypes.func,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onBlur: PropTypes.func,
    onCancel: PropTypes.func,
    onChange: PropTypes.func,
    onEditStateChange: PropTypes.func,
    onFocus: PropTypes.func,
    onSave: PropTypes.func,
    inputType: PropTypes.oneOf(['textfield, textarea']),
    className: PropTypes.string,
    style: StylePropType,
};
export default forwardRef((props, ref) => React.createElement(InlineEdit, Object.assign({}, props, { forwardedRef: ref })));
